import React, { useState, useEffect } from 'react';
import '../../SupplierOutstandingPayments.css';
import supplierPaymentService from '../../../services/supplierPaymentService';
import supplierService from '../../../services/supplierService';

const SupplierOutstandingPayments = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [outstandingSummary, setOutstandingSummary] = useState(null);
  const [supplierOutstanding, setSupplierOutstanding] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState('');

  // Search and Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('remaining_amount');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchSupplierOutstanding();
    fetchSuppliers();
  }, []);

  const fetchSupplierOutstanding = async () => {
    setLoading(true);
    try {
      const response = await supplierPaymentService.getSupplierOutstandingPayments();
      setSupplierOutstanding(response || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch outstanding payments');
      console.error('Error fetching outstanding payments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await supplierService.getActiveSuppliers();
      setSuppliers(response || []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  const fetchSupplierPurchaseOrders = async (supplierId) => {
    try {
      const response = await supplierPaymentService.getSupplierPurchaseOrders(supplierId);
      setPurchaseOrders(response || []);
    } catch (err) {
      console.error('Error fetching purchase orders:', err);
    }
  };

  const fetchSupplierOutstandingSummary = async (supplierId) => {
    if (!supplierId) {
      setOutstandingSummary(null);
      return;
    }
    
    setSummaryLoading(true);
    try {
      const response = await supplierPaymentService.getSupplierPaymentSummary(supplierId);
      setOutstandingSummary(response);
      setSelectedSupplier(response.supplier);
    } catch (err) {
      console.error('Error fetching outstanding summary:', err);
      setOutstandingSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchPurchaseOrderOutstanding = async (purchaseOrderId) => {
    if (!purchaseOrderId) {
      setSelectedPurchaseOrder(null);
      return;
    }
    
    try {
      const response = await supplierPaymentService.getPurchaseOrderPaymentSummary(purchaseOrderId);
      setSelectedPurchaseOrder(response);
    } catch (err) {
      console.error('Error fetching purchase order outstanding:', err);
      setSelectedPurchaseOrder(null);
    }
  };

  const handleSupplierSelect = async (supplierId) => {
    setSelectedSupplier(supplierId);
    setPurchaseOrders([]);
    setSelectedPurchaseOrder(null);
    
    if (supplierId) {
      await fetchSupplierOutstandingSummary(supplierId);
      await fetchSupplierPurchaseOrders(supplierId);
    } else {
      setOutstandingSummary(null);
    }
  };

  const handlePurchaseOrderSelect = async (purchaseOrderId) => {
    if (purchaseOrderId) {
      await fetchPurchaseOrderOutstanding(purchaseOrderId);
    } else {
      setSelectedPurchaseOrder(null);
    }
  };

  const applyFiltersAndSearch = () => {
    let result = [...supplierOutstanding];

    // Apply search filter
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(supplier =>
        supplier.supplier_name?.toLowerCase().includes(lowercasedSearch) ||
        supplier.supplier_email?.toLowerCase().includes(lowercasedSearch) ||
        supplier.supplier_phone?.toLowerCase().includes(lowercasedSearch)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'has_outstanding') {
        result = result.filter(supplier => supplier.total_remaining_amount > 0);
      } else if (statusFilter === 'no_outstanding') {
        result = result.filter(supplier => supplier.total_remaining_amount <= 0);
      }
    }

    // Apply sorting
    result.sort((a, b) => {
      let aValue = a[sortBy] || 0;
      let bValue = b[sortBy] || 0;

      // Handle numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      }

      return 0;
    });

    return result;
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LKR'
    }).format(amount || 0);
  };

  const getOutstandingStatus = (amount) => {
    if (amount === 0) return 'paid';
    if (amount > 0) return 'outstanding';
    return 'overpaid';
  };

  const getStatusBadge = (amount) => {
    const status = getOutstandingStatus(amount);
    const statusClasses = {
      paid: 'status-paid',
      outstanding: 'status-outstanding',
      overpaid: 'status-overpaid'
    };
    
    return `status-badge ${statusClasses[status] || 'status-outstanding'}`;
  };

  const getStatusText = (amount) => {
    const status = getOutstandingStatus(amount);
    const statusTexts = {
      paid: 'Paid',
      outstanding: 'Outstanding',
      overpaid: 'Overpaid'
    };
    
    return statusTexts[status] || 'Outstanding';
  };

  const filteredSuppliers = applyFiltersAndSearch();
  const totalOutstanding = supplierOutstanding.reduce((sum, supplier) => sum + (supplier.total_remaining_amount || 0), 0);

  return (
    <div className="supplier-outstanding-container">
      <div className="supplier-outstanding-header">
        <div>
          <h1>Supplier Outstanding Payments</h1>
          <p>Track and manage outstanding payments to suppliers</p>
        </div>
        <div className="outstanding-summary-card">
          <div className="summary-total">
            <span className="summary-label">Total Outstanding:</span>
            <span className="summary-amount outstanding">
              {formatCurrency(totalOutstanding)}
            </span>
          </div>
          <div className="summary-count">
            <span className="summary-label">Suppliers with Balance:</span>
            <span className="summary-count-value">
              {supplierOutstanding.filter(s => s.total_remaining_amount > 0).length}
            </span>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="supplier-outstanding-controls">
        <div className="supplier-outstanding-search-filter">
          <div className="supplier-outstanding-search-box">
            <input
              type="text"
              placeholder="Search suppliers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="supplier-outstanding-search-input"
            />
            <span className="supplier-outstanding-search-icon">🔍</span>
          </div>
          
          <div className="supplier-outstanding-filter-group">
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="supplier-outstanding-filter-select"
            >
              <option value="all">All Status</option>
              <option value="has_outstanding">Has Outstanding</option>
              <option value="no_outstanding">No Outstanding</option>
            </select>

            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="supplier-outstanding-filter-select"
            >
              <option value="supplier_name">Sort by Name</option>
              <option value="total_remaining_amount">Sort by Amount</option>
              <option value="total_purchase_amount">Sort by Total Purchases</option>
            </select>

            <button 
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="supplier-outstanding-sort-btn"
            >
              {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
            </button>
          </div>
        </div>

        <div className="supplier-outstanding-results-info">
          Showing {filteredSuppliers.length} of {supplierOutstanding.length} suppliers
          {searchTerm && (
            <span className="supplier-outstanding-search-term">
              for "{searchTerm}"
            </span>
          )}
        </div>
      </div>

      <div className="supplier-outstanding-content">
        <div className="outstanding-overview">
          <div className="suppliers-list">
            <h3>Suppliers Overview</h3>
            {loading ? (
              <div className="loading">Loading outstanding payments...</div>
            ) : filteredSuppliers.length === 0 ? (
              <div className="no-data">
                <div className="no-data-icon">📊</div>
                <h4>No suppliers found</h4>
                <p>No supplier data available or matching your search criteria.</p>
              </div>
            ) : (
              <div className="suppliers-table-container">
                <table className="suppliers-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('supplier_name')} className="sortable-header">
                        Supplier {getSortIcon('supplier_name')}
                      </th>
                      <th onClick={() => handleSort('total_purchase_amount')} className="sortable-header">
                        Total Purchases {getSortIcon('total_purchase_amount')}
                      </th>
                      <th onClick={() => handleSort('total_paid_amount')} className="sortable-header">
                        Total Paid {getSortIcon('total_paid_amount')}
                      </th>
                      <th onClick={() => handleSort('total_remaining_amount')} className="sortable-header">
                        Remaining {getSortIcon('total_remaining_amount')}
                      </th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSuppliers.map(supplier => (
                      <tr 
                        key={supplier.supplier_id} 
                        className={selectedSupplier === supplier.supplier_id ? 'selected' : ''}
                        onClick={() => handleSupplierSelect(supplier.supplier_id)}
                      >
                        <td className="supplier-name">
                          <strong>{supplier.supplier_name}</strong>
                          <div className="supplier-contact">
                            {supplier.supplier_email && <span>{supplier.supplier_email}</span>}
                            {supplier.supplier_phone && <span>• {supplier.supplier_phone}</span>}
                          </div>
                        </td>
                        <td className="amount">{formatCurrency(supplier.total_purchase_amount)}</td>
                        <td className="amount positive">{formatCurrency(supplier.total_paid_amount)}</td>
                        <td className="amount outstanding">{formatCurrency(supplier.total_remaining_amount)}</td>
                        <td>
                          <span className={getStatusBadge(supplier.total_remaining_amount)}>
                            {getStatusText(supplier.total_remaining_amount)}
                          </span>
                        </td>
                        <td>
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSupplierSelect(supplier.supplier_id);
                            }}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="supplier-details">
            {selectedSupplier && outstandingSummary ? (
              <div className="details-panel">
                <div className="details-header">
                  <h3>{outstandingSummary.supplier?.name} - Payment Details</h3>
                  <div className="supplier-info">
                    <div className="info-item">
                      <label>Contact:</label>
                      <span>{outstandingSummary.supplier?.contact_person || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <label>Email:</label>
                      <span>{outstandingSummary.supplier?.email || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <label>Phone:</label>
                      <span>{outstandingSummary.supplier?.phone || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="payment-summary">
                  <h4>Payment Summary</h4>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <label>Total Purchases:</label>
                      <span className="amount total">{formatCurrency(outstandingSummary.payment_summary?.total_purchase_amount)}</span>
                    </div>
                    <div className="summary-item">
                      <label>Total Paid:</label>
                      <span className="amount positive">{formatCurrency(outstandingSummary.payment_summary?.total_paid_amount)}</span>
                    </div>
                    <div className="summary-item">
                      <label>Remaining Balance:</label>
                      <span className={`amount ${outstandingSummary.payment_summary?.remaining_amount > 0 ? 'outstanding' : 'positive'}`}>
                        {formatCurrency(outstandingSummary.payment_summary?.remaining_amount)}
                      </span>
                    </div>
                  </div>

                  {outstandingSummary.payment_summary?.total_purchase_amount > 0 && (
                    <div className="progress-section">
                      <label>Payment Progress:</label>
                      <div className="progress-bar large">
                        <div 
                          className="progress-fill"
                          style={{ 
                            width: `${(outstandingSummary.payment_summary.total_paid_amount / outstandingSummary.payment_summary.total_purchase_amount) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <span className="progress-text">
                        {((outstandingSummary.payment_summary.total_paid_amount / outstandingSummary.payment_summary.total_purchase_amount) * 100).toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>

                <div className="purchase-orders-section">
                  <h4>Purchase Orders</h4>
                  <div className="purchase-orders-selector">
                    <select
                      value={selectedPurchaseOrder?.purchase_order?.id || ''}
                      onChange={(e) => handlePurchaseOrderSelect(e.target.value)}
                      className="purchase-order-select"
                    >
                      <option value="">Select Purchase Order to View Details</option>
                      {purchaseOrders.map(order => (
                        <option key={order.id} value={order.id}>
                          {order.po_number} - {formatCurrency(order.total_amount)} - {order.status}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedPurchaseOrder && (
                    <div className="purchase-order-details">
                      <div className="po-summary">
                        <h5>PO: {selectedPurchaseOrder.purchase_order?.po_number}</h5>
                        <div className="po-summary-grid">
                          <div className="po-summary-item">
                            <label>PO Total:</label>
                            <span className="amount">{formatCurrency(selectedPurchaseOrder.total_amount)}</span>
                          </div>
                          <div className="po-summary-item">
                            <label>Already Paid:</label>
                            <span className="amount positive">{formatCurrency(selectedPurchaseOrder.total_paid_amount)}</span>
                          </div>
                          <div className="po-summary-item">
                            <label>Remaining:</label>
                            <span className={`amount ${selectedPurchaseOrder.remaining_amount > 0 ? 'outstanding' : 'positive'}`}>
                              {formatCurrency(selectedPurchaseOrder.remaining_amount)}
                            </span>
                          </div>
                          <div className="po-summary-item full-width">
                            <label>Payment Progress:</label>
                            <div className="progress-bar">
                              <div 
                                className="progress-fill"
                                style={{ width: `${selectedPurchaseOrder.payment_progress}%` }}
                              ></div>
                            </div>
                            <span className="progress-text">{selectedPurchaseOrder.payment_progress.toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>

                      {selectedPurchaseOrder.payment_history && selectedPurchaseOrder.payment_history.length > 0 && (
                        <div className="payment-history">
                          <h6>Payment History</h6>
                          <div className="payment-history-list">
                            {selectedPurchaseOrder.payment_history.map(payment => (
                              <div key={payment.id} className="payment-history-item">
                                <div className="payment-info">
                                  <span className="payment-date">{new Date(payment.payment_date).toLocaleDateString()}</span>
                                  <span className="payment-amount positive">{formatCurrency(payment.amount)}</span>
                                  <span className="payment-method">{payment.payment_method}</span>
                                  <span className={`payment-status ${payment.status}`}>{payment.status}</span>
                                </div>
                                {payment.reference_number && (
                                  <div className="payment-reference">Ref: {payment.reference_number}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="no-selection">
                <div className="no-selection-icon">👈</div>
                <h4>Select a Supplier</h4>
                <p>Click on a supplier from the list to view detailed payment information and outstanding balances.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierOutstandingPayments;